import { Sun, Moon, Plus, X, Clock } from "lucide-react";
import { useClinic } from "@/store/ClinicContext";
import { WEEK_DAYS, normBlocks, DEFAULT_DURATION } from "@/data/mockData";

export const Disponibilidade = ({ doctorId, readOnly = false }) => {
  const { availability, toggleShift, addBlock, removeBlock, setBlockTime, durations, setDuration } = useClinic();
  const doc = availability[doctorId] || {};
  const dur = durations?.[doctorId] || DEFAULT_DURATION;

  const Shift = ({ dayKey, period, icon: Icon, label }) => {
    const blocks = normBlocks(doc[dayKey]?.[period], period);

    if (readOnly) {
      if (!blocks.length) return null;
      return (
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700">
          <Icon className="h-3.5 w-3.5" />
          {label} {blocks.map((b) => `${b.from}–${b.to}`).join(", ")}
        </span>
      );
    }

    return (
      <div className={`rounded-lg border p-2.5 ${blocks.length ? "border-blue-200 bg-blue-50/50" : "border-slate-200 bg-slate-50/60"}`}>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid={`avail-${doctorId}-${dayKey}-${period}`}
            onClick={() => toggleShift(doctorId, dayKey, period)}
            className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold transition-colors ${blocks.length ? "bg-blue-600 text-white" : "bg-white text-slate-400 ring-1 ring-slate-200"}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
          {blocks.length > 0 && (
            <button
              type="button"
              data-testid={`avail-${doctorId}-${dayKey}-${period}-add`}
              onClick={() => addBlock(doctorId, dayKey, period)}
              className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
            >
              <Plus className="h-3.5 w-3.5" /> horário
            </button>
          )}
        </div>
        {blocks.map((b, i) => (
          <div key={i} className="mt-2 flex items-center gap-1 text-xs text-slate-500">
            <span>De</span>
            <input type="time" data-testid={`avail-${doctorId}-${dayKey}-${period}-block-${i}-from`} value={b.from} onChange={(e) => setBlockTime(doctorId, dayKey, period, i, "from", e.target.value)} className="rounded-md border border-slate-200 bg-white px-1.5 py-1 font-mono-num text-xs text-slate-700 focus:border-blue-400 focus:outline-none" />
            <span>às</span>
            <input type="time" data-testid={`avail-${doctorId}-${dayKey}-${period}-block-${i}-to`} value={b.to} onChange={(e) => setBlockTime(doctorId, dayKey, period, i, "to", e.target.value)} className="rounded-md border border-slate-200 bg-white px-1.5 py-1 font-mono-num text-xs text-slate-700 focus:border-blue-400 focus:outline-none" />
            <button type="button" data-testid={`avail-${doctorId}-${dayKey}-${period}-block-${i}-remove`} onClick={() => removeBlock(doctorId, dayKey, period, i)} className="ml-1 text-slate-300 transition-colors hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid={`availability-${doctorId}`}>
      {!readOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
          <Clock className="h-4 w-4 text-blue-600" />
          <span className="text-xs font-semibold text-slate-600">Duração padrão da consulta</span>
          <input type="number" min="10" step="5" data-testid={`avail-${doctorId}-duration`} value={dur} onChange={(e) => setDuration(doctorId, parseInt(e.target.value, 10) || 30)} className="w-16 rounded-md border border-slate-200 bg-white px-2 py-1 font-mono-num text-xs text-slate-700 focus:border-blue-400 focus:outline-none" />
          <span className="text-xs text-slate-400">min</span>
        </div>
      )}
      {WEEK_DAYS.map((d) => {
        const mb = normBlocks(doc[d.key]?.manha, "manha");
        const tb = normBlocks(doc[d.key]?.tarde, "tarde");
        const fechado = readOnly && !mb.length && !tb.length;
        return (
          <div key={d.key} className="flex flex-col gap-2 border-b border-slate-100 pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-start">
            <span className="w-24 shrink-0 pt-1 text-sm font-semibold text-slate-700">{d.label}</span>
            <div className="flex flex-wrap items-start gap-2">
              <Shift dayKey={d.key} period="manha" icon={Sun} label="Manhã" />
              <Shift dayKey={d.key} period="tarde" icon={Moon} label="Tarde" />
              {fechado && <span className="pt-1 text-xs text-slate-300">Agenda fechada</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};
